import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { useState } from "react";
import styled from "styled-components";
import { IBenefitSelectionView } from "types/BenefitSelection/BenefitSelection";

import { SectionTitle, Surface } from "./Surface";

const StyledMessage = styled(Message)`
  justify-content: flex-start;
`;

const ChecklistRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--surface-200);

  &:last-child {
    border-bottom: none;
  }
`;

export type EngineState = "OFF" | "ARMED" | "ON";

/**
 * The account's engine state, from the two locks.
 *
 * 🚨 THE ACCOUNT FLAG DOES NOT MOVE AN ESTIMATE ON ITS OWN. The system-wide switch is a deploy-time
 * setting and no environment holds the value that enables per-account routing today, so an account
 * whose flag is set is ARMED, not ON. Reporting it as ON would tell somebody their change had
 * reached patients when it had not.
 */
export const engineStateOf = (
  engine: IBenefitSelectionView["engine"]
): EngineState => {
  if (!engine.accountFlag) return "OFF";
  return engine.effective === "ENGINE" ? "ON" : "ARMED";
};

const STATE_TEXT: Record<EngineState, string> = {
  OFF: "Off for this account. Estimates do not use these settings until it is turned on.",
  ARMED:
    "Armed for this account. Takes effect when the system-wide engine switch is on; until then estimates do not use these settings.",
  ON: "On for this account. Every estimate on this account uses these settings.",
};

interface IEngineStatusLineProps {
  engine: IBenefitSelectionView["engine"];
  /** Null when nobody has chosen a service line — one of the three checklist rows. */
  serviceLineChosen: boolean;
  /** Whether the current user may flip the switch. Non-super-admins see the state, not the button. */
  canEdit: boolean;
  saving: boolean;
  onSetEnabled: (enabled: boolean) => void;
}

/**
 * CLE-2792 — one status line, and the only question it answers is whether the engine is on here.
 *
 * The checklist inside the confirm is READ ONLY and deliberately does not block: two of its three
 * rows can be red and turning on is still the right thing to do, because arming an account is how
 * the system-wide switch gets something to switch. What the dialog owes is that the reader knows
 * which of the two locks they are moving.
 *
 * ⚠️ NO STATE PILL (review round 3). The sentence says what the state is and the button says what
 * pressing it would do; a pill saying "Armed" beside a button saying "Turn off the engine…" was a
 * third rendering of one fact, and the shortest of the three — which is the one that gets read.
 */
export function EngineStatusLine({
  engine,
  serviceLineChosen,
  canEdit,
  saving,
  onSetEnabled,
}: IEngineStatusLineProps) {
  const [confirming, setConfirming] = useState(false);
  const state = engineStateOf(engine);
  const turningOn = state === "OFF";

  return (
    <div
      className="flex align-items-center justify-content-between gap-3 p-3 border-round surface-50 border-1 border-gray-200"
      data-testid="benefitSelection.engineStatus"
    >
      <div className="flex flex-column gap-1">
        <SectionTitle>Engine</SectionTitle>
        <span
          className="text-sm text-color-secondary"
          data-testid="benefitSelection.engineStatus.text"
        >
          {STATE_TEXT[state]}
        </span>
      </div>

      <div className="flex align-items-center gap-3">
        {canEdit && (
          <Button
            type="button"
            className="p-button-outlined p-button-sm"
            label={turningOn ? "Turn on the engine…" : "Turn off the engine…"}
            data-testid="benefitSelection.engineStatus.toggle"
            onClick={() => setConfirming(true)}
          />
        )}
      </div>

      <Dialog
        header={
          turningOn
            ? "Turn on the engine for this account?"
            : "Turn off the engine for this account?"
        }
        visible={confirming}
        onHide={() => setConfirming(false)}
        style={{ width: "min(38rem, 95vw)" }}
        data-testid="benefitSelection.engineDialog"
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              type="button"
              label="Cancel"
              className="p-button-text p-button-sm"
              data-testid="benefitSelection.engineDialog.cancel"
              onClick={() => setConfirming(false)}
            />
            <Button
              className="p-button-sm"
              type="button"
              label={turningOn ? "Turn on" : "Turn off"}
              loading={saving}
              // 🚨 THE ONLY BLOCKING ROW. An account with no service line answers no copay, no
              // co-insurance and no physician copay, so turning the engine on for it is not an
              // early cutover — it is switching the benefits off. The system-wide switch being off
              // does NOT block: arming an account is exactly how that switch gets something to
              // switch, and the status line says Armed until it moves.
              disabled={turningOn && !serviceLineChosen}
              data-testid="benefitSelection.engineDialog.confirm"
              onClick={() => {
                onSetEnabled(turningOn);
                setConfirming(false);
              }}
            />
          </div>
        }
      >
        <Surface className="flex flex-column gap-3">
          <p className="m-0 text-sm">
            {turningOn
              ? "From the next eligibility check, this account's benefits come from the settings on this page. Estimates already sent keep the benefits they were run with."
              : "From the next eligibility check, this account's benefits go back to the previous selection. Estimates already sent keep the benefits they were run with."}
          </p>

          <div className="flex flex-column">
            <ChecklistRow>
              <span className="text-sm">
                System-wide engine switch (set at deploy)
              </span>
              <Tag
                value={engine.effective === "ENGINE" ? "On" : "Off"}
                severity={engine.effective === "ENGINE" ? "success" : "danger"}
                data-testid="benefitSelection.engineDialog.globalSwitch"
              />
            </ChecklistRow>
            <ChecklistRow>
              <span className="text-sm">
                Service line confirmed by a person
              </span>
              <Tag
                value={serviceLineChosen ? "Yes" : "No"}
                severity={serviceLineChosen ? "success" : "danger"}
                data-testid="benefitSelection.engineDialog.serviceLineChosen"
              />
            </ChecklistRow>
            <ChecklistRow>
              <span className="text-sm">This account&apos;s engine flag</span>
              <Tag
                value={engine.accountFlag ? "Set" : "Not set"}
                severity={engine.accountFlag ? "success" : undefined}
                data-testid="benefitSelection.engineDialog.accountFlag"
              />
            </ChecklistRow>
          </div>

          {turningOn && engine.effective !== "ENGINE" && (
            <StyledMessage
              severity="warn"
              data-testid="benefitSelection.engineDialog.armedWarning"
              text="Turning on saves this account's setting now; it takes effect when the system-wide switch is on, and the status line reads Armed until then."
            />
          )}

          {turningOn && !serviceLineChosen && (
            <StyledMessage
              severity="error"
              data-testid="benefitSelection.engineDialog.noServiceLine"
              text="Choose a service line first. Without one the engine answers no copay, no co-insurance and no physician copay on this account."
            />
          )}
        </Surface>
      </Dialog>
    </div>
  );
}
